import * as Sentry from "@sentry/react";
import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useMutation } from "react-relay";
import { GENERATE_JWT_FOR_USER_MUTATION } from "./use-auth-context.graphql";
import type { AvailableSocialAuthProviders, LoginVariant } from "./use-auth-context.types";
import type { useAuthContext_GenerateJWTForUserMutation } from "../../__generated__/useAuthContext_GenerateJWTForUserMutation.graphql";
import { STORAGE_KEY_LAST_LOGIN_METHOD, STORAGE_KEY_WAITING_FOR_TOKEN } from "../../consts";
import { supabase } from "../../network/supabase-client";
import { setAppTokens, clearAppTokens } from "../../store/slices/AuthSlice";
import { clearCurrentUser } from "../../store/slices/CurrentUserSlice";
import { ReduxStore } from "../../store/store.redux";

export const logout = async () => {
	ReduxStore.dispatch(clearAppTokens());
	ReduxStore.dispatch(clearCurrentUser());
	localStorage.removeItem(STORAGE_KEY_WAITING_FOR_TOKEN);

	try {
		await supabase.auth.signOut({ scope: "local" });
	} catch (error) {
		Sentry.captureException(error, {
			level: "info",
			tags: { context: "supabase_signout" },
		});
	} finally {
		for (const key of Object.keys(localStorage)) {
			if (/^sb-.*-auth-token$/.test(key)) {
				localStorage.removeItem(key);
			}
		}
		window.location.reload();
	}
};

export const useAuthContext = () => {
	const [session, setSession] = useState<Session | null>(null);

	useEffect(() => {
		const getSession = async () => {
			const {
				data: { session },
			} = await supabase.auth.getSession();
			setSession(session);
		};

		getSession();

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(() => {
			getSession();
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const [lastVariant, setLastVariantState] = useState<LoginVariant | undefined>(() => {
		return (localStorage.getItem(STORAGE_KEY_LAST_LOGIN_METHOD) as LoginVariant) || undefined;
	});

	const setLastLoginMethod = useCallback((variant: LoginVariant) => {
		setLastVariantState(variant);
		localStorage.setItem(STORAGE_KEY_LAST_LOGIN_METHOD, variant);
	}, []);

	const isLastLoginMethod = useCallback(
		(variant: LoginVariant) => lastVariant === variant,
		[lastVariant],
	);

	const signInWithPassword = async (email: string, password: string) => {
		const { data, error } = await supabase.auth.signInWithPassword({
			email,
			password,
		});

		if (error) {
			throw error;
		}

		return data;
	};

	const signInWithOAuth = async (provider: AvailableSocialAuthProviders) => {
		const redirectUrl = `${window.location.origin}${window.location.pathname}`;

		localStorage.setItem(STORAGE_KEY_WAITING_FOR_TOKEN, "true");

		const oauthOptions: {
			redirectTo: string;
			queryParams?: { prompt: string };
		} = {
			redirectTo: redirectUrl,
		};

		if (provider === "google") {
			oauthOptions.queryParams = {
				prompt: "select_account",
			};
		}

		const { data, error } = await supabase.auth.signInWithOAuth({
			provider,
			options: oauthOptions,
		});

		if (error) {
			// Clear loading state on error
			localStorage.removeItem(STORAGE_KEY_WAITING_FOR_TOKEN);
			throw error;
		}

		return data;
	};

	const signInWithOneTimePassword = async (email: string) => {
		const { data, error } = await supabase.auth.signInWithOtp({
			email,
			options: {
				shouldCreateUser: true,
			},
		});

		if (error) {
			throw error;
		}

		return data;
	};

	const verifyOtp = async (email: string, token: string) => {
		const { data, error } = await supabase.auth.verifyOtp({
			email,
			token,
			type: "email",
		});

		if (error) {
			throw error;
		}

		return data;
	};

	const getSession = async () => {
		const {
			data: { session },
		} = await supabase.auth.getSession();
		return session;
	};

	const logoutHandler = async () => {
		await logout();
	};

	const forgotPassword = async (email: string) => {
		const { error } = await supabase.auth.resetPasswordForEmail(email, {
			redirectTo: `${window.location.origin}/reset-password`,
		});

		if (error) {
			throw error;
		}
	};

	const resetPassword = async (newPassword: string) => {
		const { data, error } = await supabase.auth.updateUser({
			password: newPassword,
		});

		if (error) {
			throw error;
		}

		return data;
	};

	const dispatch = useDispatch();
	const [handleGenerateAppJwt] = useMutation<useAuthContext_GenerateJWTForUserMutation>(
		GENERATE_JWT_FOR_USER_MUTATION,
	);

	const generateAppJwt = (
		userId: string,
		accountId: string,
	): Promise<useAuthContext_GenerateJWTForUserMutation["response"]> => {
		return new Promise((resolve, reject) => {
			handleGenerateAppJwt({
				variables: {
					input: {
						userId,
						accountId,
						deviceKind: "Admin",
					},
				},
				onCompleted: (response) => {
					const tokens = response.Auth?.generateJWTForUser?.jwtTokens;
					if (tokens?.accessToken && tokens?.refreshToken) {
						dispatch(
							setAppTokens({
								accessToken: tokens.accessToken,
								refreshToken: tokens.refreshToken,
							}),
						);
					}
					resolve(response);
				},
				onError: reject,
			});
		});
	};

	return {
		login: signInWithPassword,
		signInWithPassword,
		signInWithOAuth,
		signInWithOneTimePassword,
		verifyOtp,
		session,
		getSession,
		logout: logoutHandler,
		forgotPassword,
		resetPassword,
		generateAppJwt,
		lastLoginMethod: lastVariant,
		setLastLoginMethod,
		isLastLoginMethod,
	};
};
